import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, buttonVariants } from "@cloch-ai/ui";
import { Check, Copy, Loader2 } from "lucide-react";

import type { AuthStatusAction } from "../lib/auth-status";
import { cn } from "../lib/utils";

async function copyText(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  if (typeof window !== "undefined") {
    window.prompt("Copy the recovery text below:", text);
    return true;
  }
  return false;
}

export function AuthIssueActions({
  actions,
  onRefresh,
  maxActions,
}: {
  actions: AuthStatusAction[];
  onRefresh?: () => Promise<unknown> | void;
  maxActions?: number;
}) {
  const [copiedActionId, setCopiedActionId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const visibleActions = typeof maxActions === "number" ? actions.slice(0, maxActions) : actions;

  const handleCopy = async (action: AuthStatusAction) => {
    if (action.kind !== "copy-command" && action.kind !== "copy-text") {
      return;
    }
    const text = action.kind === "copy-command" ? action.command : action.text;
    try {
      const copied = await copyText(text);
      setCopiedActionId(copied ? action.id : null);
      if (copied) {
        setTimeout(
          () => setCopiedActionId((current) => (current === action.id ? null : current)),
          2000,
        );
      }
    } catch {
      setCopiedActionId(null);
    }
  };

  const handleRefresh = async () => {
    if (!onRefresh) {
      return;
    }
    try {
      setRefreshing(true);
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {visibleActions.map((action) => {
        if (action.kind === "navigate") {
          return (
            <Link
              key={action.id}
              to={action.href}
              className={cn(buttonVariants({ size: "sm", variant: "outline" }))}
            >
              {action.label}
            </Link>
          );
        }

        if (action.kind === "refresh-status") {
          return (
            <Button
              key={action.id}
              size="sm"
              variant="outline"
              disabled={!onRefresh || refreshing}
              onClick={() => {
                void handleRefresh();
              }}
            >
              {refreshing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              {action.label}
            </Button>
          );
        }

        const copied = copiedActionId === action.id;
        return (
          <Button
            key={action.id}
            size="sm"
            variant="outline"
            onClick={() => {
              void handleCopy(action);
            }}
          >
            {copied ? (
              <Check className="mr-1 h-3.5 w-3.5" />
            ) : (
              <Copy className="mr-1 h-3.5 w-3.5" />
            )}
            {copied ? "Copied" : action.label}
          </Button>
        );
      })}
    </div>
  );
}
